import React, { useEffect, useMemo, useState } from "react";
import { getBookings, getTours } from "@/api";
import { STATUSES, fmtMxn, StatusBadge, inputCls, EXTRA_LABELS } from "./adminShared";
import BookingDetail from "./BookingDetail";
import { RefreshCw, Search } from "lucide-react";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fTour, setFTour] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([getBookings(), getTours()])
      .then(([b, t]) => {
        setBookings(b);
        setTours(t);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onUpdated = (updated, deletedId) => {
    if (deletedId) {
      setBookings((bs) => bs.filter((x) => x.id !== deletedId));
      return;
    }
    setBookings((bs) => bs.map((x) => (x.id === updated.id ? updated : x)));
    setSelected(updated);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return bookings.filter((b) => {
      if (fStatus !== "all" && b.status !== fStatus) return false;
      if (fTour !== "all" && b.tour_slug !== fTour) return false;
      if (fFrom && b.date < fFrom) return false;
      if (fTo && b.date > fTo) return false;
      if (needle && !`${b.name} ${b.email} ${b.whatsapp}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [bookings, q, fStatus, fTour, fFrom, fTo]);

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Operaciones</p>
          <h1 className="font-serif-hc font-light text-3xl md:text-4xl text-[#F5F2EA]">Reservas</h1>
        </div>
        <button data-testid="admin-refresh" onClick={load} disabled={loading} className="hc-btn-secondary inline-flex items-center gap-2">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div data-testid="bookings-filters" className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <label className="relative lg:col-span-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A095]" />
          <input
            data-testid="filter-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar cliente…"
            className={`${inputCls} pl-9`}
          />
        </label>
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={inputCls}>
          <option value="all" className="bg-[#080D0B]">Todos los estados</option>
          {Object.entries(STATUSES).map(([k, s]) => <option key={k} value={k} className="bg-[#080D0B]">{s.label}</option>)}
        </select>
        <select data-testid="filter-tour" value={fTour} onChange={(e) => setFTour(e.target.value)} className={inputCls}>
          <option value="all" className="bg-[#080D0B]">Todos los tours</option>
          {tours.map((t) => <option key={t.id} value={t.slug} className="bg-[#080D0B]">{t.name?.es}</option>)}
        </select>
        <input data-testid="filter-from" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className={inputCls} title="Desde" />
        <input data-testid="filter-to" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className={inputCls} title="Hasta" />
      </div>

      {/* Tabla */}
      <div className="mt-6 overflow-x-auto border border-[#1E2B25]">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[#0C1310] text-left">
              {["Cliente", "Tour", "Fecha", "Personas", "Extras", "Total", "Saldo", "Estado"].map((h) => (
                <th key={h} className="p-4 eyebrow font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => {
              const balance = (b.total_price_mxn || 0) - (b.amount_paid || 0);
              return (
                <tr
                  key={b.id}
                  data-testid={`booking-row-${b.id}`}
                  onClick={() => setSelected(b)}
                  className="border-t border-[#1E2B25] hover:bg-[#0C1310]/70 cursor-pointer transition-colors"
                >
                  <td className="p-4">
                    <p className="text-[#F5F2EA]">{b.name}</p>
                    <p className="text-xs text-[#A3A095] mt-0.5">{b.email}</p>
                  </td>
                  <td className="p-4 text-[#F5F2EA]">{b.tour_name}</td>
                  <td className="p-4 text-[#F5F2EA] whitespace-nowrap">{b.date}{b.schedule ? ` · ${b.schedule}` : ""}</td>
                  <td className="p-4 text-[#F5F2EA] whitespace-nowrap">{b.adults} ad. / {b.children} ni.</td>
                  <td className="p-4 text-xs text-[#C4A661]">
                    {b.extras?.length ? b.extras.map((e) => EXTRA_LABELS[e] || e).join(" + ") : <span className="text-[#A3A095]">—</span>}
                  </td>
                  <td className="p-4 text-[#C4A661] whitespace-nowrap">{fmtMxn(b.total_price_mxn)}</td>
                  <td className={`p-4 whitespace-nowrap ${balance > 0 ? "text-[#C4A661]" : "text-[#7BC47F]"}`}>{fmtMxn(balance)}</td>
                  <td className="p-4"><StatusBadge status={b.status} /></td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-[#A3A095]" data-testid="bookings-empty">
                  {loading ? "Cargando…" : "No hay reservas que coincidan con los filtros."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BookingDetail booking={selected} open={!!selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />
    </div>
  );
}
